"use client"
import type React from "react"
import Image from "next/image"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { useState, useRef, useEffect } from "react"
import { Plus_Jakarta_Sans } from "next/font/google"
import { useGetEcosystemGroupsQuery } from "@/services/ecoType/queries"

/* Cấu hình font chữ sử dụng cho header */
const plusJakarta = Plus_Jakarta_Sans({
    subsets: ["latin", "latin-ext", "vietnamese"],
    weight: ["500", "600", "700"],
    variable: "--font-plus-jakarta",
    display: "swap",
})

/* Định nghĩa props cho component header */
interface LandingHeaderProps {
    hideAction?: boolean
}

/* Component header chính của landing page */
const LandingHeader: React.FC<LandingHeaderProps> = ({ hideAction = false }) => {
    /* Quản lý state giao diện và tham chiếu DOM */
    const pathname = usePathname()
    const [showEcosystem, setShowEcosystem] = useState(false)
    const ecosystemBtnRef = useRef<HTMLButtonElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const [arrowLeft, setArrowLeft] = useState(0)

    /* State quản lý menu trên mobile */
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const [expandMobileEco, setExpandMobileEco] = useState(false)

    /* Cập nhật vị trí mũi tên của menu hệ sinh thái */
    useEffect(() => {
        const updatePosition = () => {
            if (showEcosystem && ecosystemBtnRef.current) {
                const rect = ecosystemBtnRef.current.getBoundingClientRect()
                setArrowLeft(rect.left + rect.width / 2)
            }
        }
        updatePosition()
        window.addEventListener("resize", updatePosition)
        return () => window.removeEventListener("resize", updatePosition)
    }, [showEcosystem])

    /* Đóng menu hệ sinh thái khi click ra ngoài */
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                showEcosystem &&
                menuRef.current &&
                !menuRef.current.contains(event.target as Node) &&
                ecosystemBtnRef.current &&
                !ecosystemBtnRef.current.contains(event.target as Node)
            ) {
                setShowEcosystem(false)
            }
        }

        document.addEventListener("mousedown", handleClickOutside)
        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
        }
    }, [showEcosystem])

    /* Khóa scroll body khi mở menu mobile */
    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = 'unset'
        }
        return () => { document.body.style.overflow = 'unset' }
    }, [mobileMenuOpen])

    /* Lấy dữ liệu nhóm hệ sinh thái từ API */
    const { data: groups = [], isLoading: groupsLoading, isError } = useGetEcosystemGroupsQuery()

    /* Dữ liệu fallback khi API lỗi hoặc chưa có dữ liệu */
    const fallbackCompanies = [
        {
            id: "travel",
            name: "Rovi Travel",
            description: "Du lịch khách sạn",
            icon: "travel",
            logo: "/images/HST/RVTV.png"
        },
        {
            id: "hospitality",
            name: "Rovi Hospitality",
            description: "Dịch vụ tư vấn khách sạn nhà hàng",
            icon: "hospitality",
            logo: "/images/HST/RVHOP.png"
        },
        {
            id: "logistics",
            name: "Rovi Logistics",
            description: "Dịch vụ vận tải",
            icon: "logistics",
            logo: "/images/HST/RVLOG.png"
        },
        {
            id: "investment",
            name: "Rovi Investment",
            description: "Dịch vụ đầu tư",
            icon: "investment",
            logo: "/images/HST/RVINV.png"
        },
    ]

    // --- Helper tạo Slug chuẩn (đồng bộ với trang chi tiết) ---
    const createSlug = (title: string) => {
        return title.toLowerCase()
            .replace(/\(rvh\)/g, '')
            .trim()
            .replace(/[áàảãạăắằẳẵặâấầẩẫậ]/g, 'a')
            .replace(/[éèẻẽẹêếềểễệ]/g, 'e')
            .replace(/[iíìỉĩị]/g, 'i')
            .replace(/[óòỏõọôốồổỗộơớờởỡợ]/g, 'o')
            .replace(/[úùủũụưứừửữự]/g, 'u')
            .replace(/[ýỳỷỹỵ]/g, 'y')
            .replace(/đ/g, 'd')
            .replace(/[^a-z0-9-]/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
    };

    /* Chuẩn hóa dữ liệu hệ sinh thái cho menu */
    const ecosystemCompanies = groups.length > 0 && !isError && !groupsLoading
        ? groups.map((group) => {
            const name = group.title.replace(" (RVH)", "").trim() || "Đang cập nhật"
            // Sử dụng hàm createSlug chuẩn
            const slug = createSlug(group.title)
            return {
                id: group.id.toString(),
                name,
                description: group.description || "Thông tin đang được cập nhật...",
                icon: "default",
                logo: group.image || "/images/HST/logo-holding.png",
                slug,
            }
        })
        : fallbackCompanies.map((company) => ({
            ...company,
            // Tạo slug từ tên fallback
            slug: createSlug(company.name),
        }))

    /* Danh sách menu điều hướng chính */
    const menuItems = [
        { href: "/", label: "Trang Chủ" },
        { href: "/about", label: "Về Chúng Tôi" },
        { href: "/ecosystem", label: "Hệ Sinh Thái" },
        { href: "/news", label: "Tin Tức" },
        { href: "/contact", label: "Liên Hệ" },
    ]

    return (
        <>
            {/* Thanh điều hướng chính */}
            <nav className="sticky top-0 bg-white z-50 shadow-sm md:shadow-none">
                <div className={`w-full px-4 md:px-5 lg:px-6 xl:px-8 2xl:px-13 py-2 md:py-1 lg:py-1.5 flex items-center justify-between md:justify-start ${plusJakarta.variable}`}>

                    {/* Logo thương hiệu */}
                    <Link href="/" className="flex-shrink-0">
                        <Image
                            src="/images/logo Rovi Holdings.png"
                            alt="RVH Logo"
                            width={250}
                            height={60}
                            className="h-8 w-auto md:h-9 lg:h-10 xl:h-11 2xl:h-12"
                            priority
                        />
                    </Link>

                    {/* Nút mở menu mobile */}
                    <button
                        className="md:hidden p-2 text-gray-700 focus:outline-none"
                        onClick={() => setMobileMenuOpen(true)}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                        </svg>
                    </button>

                    {/* Menu điều hướng desktop */}
                    <div className="hidden md:flex items-center gap-4 ml-4">
                        <div className="h-4 sm:h-5 md:h-5 lg:h-12 w-px bg-gray-300 scale-x-50" aria-hidden="true" />
                        <div className="flex items-center gap-7 text-sm relative">
                            {menuItems.map((item) => {
                                const isEcosystem = item.label === "Hệ Sinh Thái"

                                /* Xác định trạng thái active của menu */
                                const isActive = isEcosystem
                                    ? pathname === item.href || pathname.startsWith("/ecosystem/")
                                    : pathname === item.href

                                const commonClasses = `relative pb-0 border-b transition leading-[14px] tracking-normal text-black font-semibold flex items-center gap-1 ${isActive
                                    ? "border-[#006633] text-[#006633]"
                                    : "border-transparent hover:border-[#006633] hover:text-[#006633]"
                                    }`
                                return (
                                    <div key={item.href}>
                                        {isEcosystem ? (
                                            <button
                                                ref={ecosystemBtnRef}
                                                onClick={() => setShowEcosystem(!showEcosystem)}
                                                className={commonClasses}
                                                style={{ fontSize: "11px" }}
                                            >
                                                {item.label}
                                            </button>
                                        ) : (
                                            <Link
                                                href={item.href}
                                                className={commonClasses}
                                                style={{ fontSize: "11px" }}
                                            >
                                                {item.label}
                                            </Link>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>
            </nav>

            {/* Lớp phủ menu mobile */}
            {mobileMenuOpen && (
                <div className={`fixed inset-0 z-[100] bg-white overflow-y-auto ${plusJakarta.variable}`}>
                    <div className="flex flex-col min-h-screen">
                        {/* Header menu mobile */}
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                            <Link href="/" onClick={() => setMobileMenuOpen(false)}>
                                <Image
                                    src="/images/logo Rovi Holdings.png"
                                    alt="RVH Logo"
                                    width={180}
                                    height={50}
                                    className="h-8 w-auto"
                                />
                            </Link>
                            <button
                                onClick={() => setMobileMenuOpen(false)}
                                className="p-2 text-gray-500 hover:text-red-500"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Danh sách menu mobile */}
                        <div className="flex flex-col p-4 space-y-4">
                            {menuItems.map((item) => {
                                const isEcosystem = item.label === "Hệ Sinh Thái"
                                const isActive = pathname === item.href

                                if (isEcosystem) {
                                    return (
                                        <div key={item.href} className="border-b border-gray-100 pb-2">
                                            <button
                                                onClick={() => setExpandMobileEco(!expandMobileEco)}
                                                className="flex w-full items-center justify-between py-2 text-lg font-bold text-gray-800"
                                            >
                                                <span>{item.label}</span>
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"
                                                    className={`w-5 h-5 transition-transform ${expandMobileEco ? 'rotate-180' : ''}`}
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                                </svg>
                                            </button>

                                            {/* Danh sách công ty hệ sinh thái trên mobile */}
                                            {expandMobileEco && (
                                                <div className="flex flex-col pl-4 mt-2 space-y-3 border-l-2 border-gray-100 animate-in slide-in-from-top-2">
                                                    {ecosystemCompanies.map((company) => (
                                                        <Link
                                                            key={company.id}
                                                            href={`/ecosystem/${company.slug}`}
                                                            onClick={() => setMobileMenuOpen(false)}
                                                            className="text-base font-medium text-gray-600 hover:text-[#006633] py-1"
                                                        >
                                                            {company.name}
                                                        </Link>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )
                                }

                                return (
                                    <Link
                                        key={item.href}
                                        href={item.href}
                                        onClick={() => setMobileMenuOpen(false)}
                                        className={`block py-2 text-lg font-bold border-b border-gray-100 ${isActive ? 'text-[#006633]' : 'text-gray-800'
                                            }`}
                                    >
                                        {item.label}
                                    </Link>
                                )
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Mega menu hệ sinh thái trên desktop */}
            {showEcosystem && (
                <div className="hidden md:block" ref={menuRef}>
                    <div
                        className="fixed top-[69px] w-4 h-4 bg-white border-t border-l border-gray-100 z-[60]"
                        style={{
                            left: arrowLeft,
                            transform: 'translateX(-50%) rotate(45deg)'
                        }}
                    />
                    <div className={`fixed top-[76px] left-1/2 transform -translate-x-1/2 z-50 w-[95%] max-w-5xl bg-white rounded-2xl shadow-xl border border-gray-100 ${plusJakarta.variable} animate-in fade-in slide-in-from-top-2 duration-200`}>
                        <div className="relative bg-white rounded-2xl overflow-hidden">
                            <div className="py-3 px-4 mb-0 text-[9px] md:text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                                HỆ SINH THÁI ROVI HOLDINGS
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200">
                                {ecosystemCompanies.map((company, index) => (
                                    <Link
                                        key={index}
                                        href={`/ecosystem/${company.slug}`}
                                        onClick={() => setShowEcosystem(false)}
                                        className="group block px-4 py-2 md:px-6 flex flex-col justify-between h-full bg-white hover:bg-gray-50 transition-colors duration-200"
                                    >
                                        <div className="flex gap-3 mb-1">
                                            <div className="flex-shrink-0 mt-0.5">
                                                <Image
                                                    src="/images/Behavioral.png"
                                                    alt="icon"
                                                    width={20}
                                                    height={20}
                                                    className="w-5 h-5 object-contain"
                                                />
                                            </div>
                                            <div className="flex flex-col min-w-0">
                                                <h3 className="text-sm font-bold text-gray-900 leading-tight mb-1" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                                                    {company.name}
                                                </h3>
                                                <p className="text-[11px] md:text-xs text-gray-500 font-normal leading-snug" style={{ fontFamily: '"Helvetica Neue", Roboto, sans-serif' }}>
                                                    {company.description}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-center mt-auto w-full h-24">
                                            <Image
                                                src={company.logo}
                                                alt={`${company.name} logo`}
                                                width={220}
                                                height={110}
                                                className="max-h-full w-auto max-w-[180px] object-contain opacity-80 group-hover:opacity-100 transition-opacity mx-auto"
                                            />
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default LandingHeader